import { get, post } from 'config/api/config';

import { MoneyGramAccountInfo, PaymentType } from 'types/payments';

const PAYMENTS_ENDPOINTS = '/payments';

export const getPaymentConfirmationDetails = async () => {
  try {
    const response = await post(PAYMENTS_ENDPOINTS);
    return response;
  } catch (error: any) {
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};

export const getPaymentTypes = async (amount: number, responseId: string) => {
  if (amount === null || amount === undefined) {
    throw new Error('Please provide valid amount.');
  }
  try {
    const response = await post<PaymentType[]>('viewer/payment-types', {
      paymentAmount: amount,
      responseId,
    });
    return response;
  } catch (error: any) {
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};

interface PlaidLinkTokenResponse {
  environment: string;
  expiration: string;
  linkToken: string;
  status: boolean;
  url: string;
}
export const getPlaidLinkToken = async (responseId: string) => {
  try {
    const response = await post<PlaidLinkTokenResponse>('viewer/plaid/link-token', {
      callbackURL: window.location.href,
      responseId,
    });
    return response;
  } catch (error: any) {
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};

interface CardRequest {
  nameOnCard: string;
  secureToken: string;
  maskedCardNumber: string;
  expMonth: number;
  expYear: number;
  cvv: number;
  postalCode: number;
  nickname: string;
  typeId: number;
  isCreditCard: boolean;
  isDebitCard: boolean;
}

interface ACHRequest {
  linkSessionId: string;
  accountIds: string[];
  institutionId: string | null;
  institutionName: string | null;
  publicToken: string;
  verificationStatus: string;
}

export type PaymentScreening = {
  applicant_name_first: string;
  applicant_name_middle: string;
  dont_have_middle_name: boolean;
  applicant_name_last: string;
  applicant_birth_date: string;
  country: string;
  street_line1: string;
  street_line2: string;
  city: string;
  state_code: string;
  zip_code: string;
  is_self_verified: boolean;
};

interface AddPaymentRequest {
  paymentTypeId: number;
  cardInfo?: CardRequest;
  bank_info?: ACHRequest;
  isScreeningEnabled?: boolean;
  paymentScreening?: PaymentScreening;
  responseId: string;
}
export const addPaymentAccount = async (data: AddPaymentRequest) => {
  try {
    const response = await post('viewer/payment-account', data);
    return response;
  } catch (error: any) {
    // Enhance the error with errorCode from the response
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};

interface MakePaymentRequest {
  paymentAmount: number;
  paymentAccountId: number;
  paymentTypeId: number;
  responseId: string;
}
interface MakePaymentResponse {
  success: boolean;
  totalAmount: number;
  errorCode?: number;
  message?: string;
  payments: {
    id: number;
    result: string;
    amount: number;
    fee: number;
    total: number;
    status: string;
    type: string;
    createdCustomerName: string;
  }[];
}

interface ErrorResponseData {
  errorCode?: string;
  errorResponse?: string; // Stringified JSON from HttpExceptionFilter
  statusCode?: number;
  message?: string;
  timestamp?: string;
  path?: string;
}

interface ParsedErrorResponse {
  code?: number;
  errorCode?: number;
  message?: string;
  errorMessage?: string;
}

export interface PaymentError extends Error {
  errorCode?: number;
  response?: {
    data?: ErrorResponseData;
  };
}

/**
 * Extracts the payment error code from the API error response.
 * The backend HttpExceptionFilter wraps errors in errorResponse as a JSON string.
 * @param responseData - The error response data from the API
 * @returns The error code if found, undefined otherwise
 */
export function extractErrorCode(responseData?: ErrorResponseData): number | undefined {
  if (!responseData) return undefined;

  // Try to parse errorResponse (stringified JSON from HttpExceptionFilter)
  if (responseData.errorResponse) {
    try {
      const parsed: ParsedErrorResponse = JSON.parse(responseData.errorResponse);
      // Check for 'code' first (from PaymentApiException), then 'errorCode' (from PaymentResponse)
      const extractedCode = parsed.code ?? parsed.errorCode;
      return extractedCode;
    } catch {
      // If parsing fails, fall through to other checks
    }
  }

  // Direct errorCode field (legacy support)
  if (responseData.errorCode) {
    const code = parseInt(responseData.errorCode, 10);
    if (!Number.isNaN(code)) {
      return code;
    }
  }

  return undefined;
}

export const makePayment = async (data: MakePaymentRequest) => {
  try {
    const response = await post<MakePaymentResponse>('viewer/payments', data);

    // Check for error codes in the response
    if (response.data?.errorCode !== undefined) {
      const error: PaymentError = new Error(
        response.data.message || 'Payment processing error',
      ) as PaymentError;
      error.errorCode = response.data.errorCode;
      error.response = {
        data: {
          errorResponse: JSON.stringify({
            code: response.data.errorCode,
            message: response.data.message,
          }),
        },
      };
      throw error;
    }

    return response;
  } catch (error: any) {
    // Enhance the error with errorCode from the response
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};

export const getPaymentSettings = async () => {
  try {
    const response = await get('viewer/payment-settings');
    return response;
  } catch (error: any) {
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};

export const getPaymentAccounts = async () => {
  try {
    const response = await get('viewer/payment-accounts');
    return response;
  } catch (error: any) {
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};

export const getMoneyGramAccount = async (responseId: string) => {
  if (!responseId) throw new Error('Missing parameter.');
  try {
    const response = await get<MoneyGramAccountInfo>(`viewer/moneygram/account/${responseId}`);
    return response?.data;
  } catch (error: any) {
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};

export const createMoneyGramAccount = async (responseId: string) => {
  if (!responseId) throw new Error('Missing parameter.');
  try {
    const response = await post<MoneyGramAccountInfo>('viewer/moneygram/create-account', {
      responseId,
    });
    return response?.data;
  } catch (error: any) {
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};

export const getMoneyGramInstructions = async (
  responseId: string,
  showSpanishInstruction = false,
) => {
  if (!responseId) throw new Error('Missing parameter.');
  try {
    const response = await get<MoneyGramAccountInfo>(
      `viewer/moneygram/instructions/${responseId}/${showSpanishInstruction}`,
    );
    return response?.data;
  } catch (error: any) {
    const enhancedError: PaymentError = error as PaymentError;
    const errorCode = extractErrorCode(error?.response?.data);
    if (errorCode !== undefined) {
      enhancedError.errorCode = errorCode;
    }
    throw enhancedError;
  }
};
